import React from 'react';
import { Card, Row, Col, Typography, Spin, Alert, Divider, Space, Button, Tag } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslationWithNamespace } from '../../../hooks/useTranslationWithNamespace';
import { FeeEstimationProps } from './WithdrawalModalTypes';
import { getEstimatedTime } from './WithdrawalModalUtils';

const { Text } = Typography;

/**
 * Fee estimation component for withdrawal modal
 */
const WithdrawalModalFeeEstimation: React.FC<FeeEstimationProps> = ({
  isEstimatingFee,
  feeEstimation,
  feeEstimationError,
  triggerFeeEstimation,
  form
}) => {
  const { t } = useTranslationWithNamespace('wallet');

  // Get current speed from form
  const currentSpeed = form.getFieldValue('speed') || 'medium';

  if (isEstimatingFee) {
    return (
      <div style={{ textAlign: 'center', padding: '16px' }}>
        <Spin size="default" />
        <div style={{ marginTop: 8 }}>
          {t('sendTransaction.estimatingFee', 'Estimating transaction fee...')}
        </div>
      </div>
    );
  }

  if (feeEstimationError) {
    return (
      <Alert
        message={t('sendTransaction.feeEstimationError', 'Fee Estimation Error')}
        description={feeEstimationError}
        type="warning"
        showIcon
        action={
          <Button
            size="small"
            type="default"
            onClick={triggerFeeEstimation}
            icon={<ReloadOutlined />}
          >
            {t('sendTransaction.retry', 'Retry')}
          </Button>
        }
      />
    );
  }

  if (feeEstimation) {
    return (
      <Card
        title={
          <Space>
            <Text strong>{t('sendTransaction.feeEstimation', 'Fee Estimation')}</Text>
            <Button
              type="text"
              icon={<ReloadOutlined />}
              size="small"
              onClick={triggerFeeEstimation}
              title={t('sendTransaction.refreshFee', 'Refresh fee estimation')}
            />
          </Space>
        }
        size="small"
      >
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Text strong>{t('sendTransaction.estimatedFee', 'Estimated Fee')}:</Text>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Text>{feeEstimation.fee || feeEstimation.data?.fee || 'N/A'} {feeEstimation.unit || feeEstimation.data?.unit}</Text>
          </Col>

          <Col span={12}>
            <Text>{t('sendTransaction.estimatedTime', 'Estimated Time')}:</Text>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Text>{getEstimatedTime(currentSpeed)}</Text>
          </Col>

          {(feeEstimation.feeInUSD || feeEstimation.data?.feeInUSD) && (
            <>
              <Col span={12}>
                <Text>{t('sendTransaction.feeInUSD', 'Fee in USD')}:</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text style={{ color: 'green' }}>
                  ${feeEstimation.feeInUSD || feeEstimation.data?.feeInUSD}
                </Text>
              </Col>
            </>
          )}

          {(feeEstimation.feeInIRT || feeEstimation.data?.feeInIRT) && (
            <>
              <Col span={12}>
                <Text>{t('sendTransaction.feeInIRT', 'Fee in IRT')}:</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text style={{ color: 'blue' }}>
                  {Number(feeEstimation.feeInIRT || feeEstimation.data?.feeInIRT).toLocaleString()} IRT
                </Text>
              </Col>
            </>
          )}

          {(feeEstimation.gasPrice || feeEstimation.data?.gasPrice) && (
            <>
              <Col span={12}>
                <Text>{t('sendTransaction.gasPrice', 'Gas Price')}:</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text>{feeEstimation.gasPrice || feeEstimation.data?.gasPrice}</Text>
              </Col>
            </>
          )}

          {(feeEstimation.gasLimit || feeEstimation.data?.gasLimit) && (
            <>
              <Col span={12}>
                <Text>{t('sendTransaction.gasLimit', 'Gas Limit')}:</Text>
              </Col>
              <Col span={12} style={{ textAlign: 'right' }}>
                <Text>{feeEstimation.gasLimit || feeEstimation.data?.gasLimit}</Text>
              </Col>
            </>
          )}

          {/* Additional fee details */}
          {(feeEstimation.details || feeEstimation.data?.details || feeEstimation.source || feeEstimation.data?.source) && (
            <>
              <Col span={24}>
                <Divider style={{ margin: '8px 0' }} />
              </Col>

              {(feeEstimation.details?.network || feeEstimation.data?.details?.network) && (
                <>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {t('sendTransaction.network', 'Network')}:
                    </Text>
                  </Col>
                  <Col span={12} style={{ textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {feeEstimation.details?.network || feeEstimation.data?.details?.network}
                    </Text>
                  </Col>
                </>
              )}

              {(feeEstimation.source || feeEstimation.data?.source) && (
                <>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {t('sendTransaction.source', 'Source')}:
                    </Text>
                  </Col>
                  <Col span={12} style={{ textAlign: 'right' }}>
                    <Tag color={
                      (feeEstimation.source === 'rpc' || feeEstimation.data?.source === 'rpc')
                        ? 'green'
                        : (feeEstimation.source === 'cache' || feeEstimation.data?.source === 'cache')
                          ? 'blue'
                          : 'orange'
                    }>
                      {feeEstimation.source || feeEstimation.data?.source || 'unknown'}
                    </Tag>
                  </Col>
                </>
              )}
            </>
          )}
        </Row>
      </Card>
    );
  }

  return (
    <Alert
      message={t('sendTransaction.noFeeEstimation', 'No Fee Estimation')}
      description={t('sendTransaction.fillFieldsForFee', 'Fill in the amount and destination address to see fee estimation')}
      type="info"
      showIcon
    />
  );
};

export default WithdrawalModalFeeEstimation;
